import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api'
import { api } from '../lib/api'
import { Loader2, MapPin } from 'lucide-react'

// Custom Marker component that handles hover events
const HoverableMarker = ({ parish, onHover, onHoverOut, onClick, icon, selectedParish, hoverTimeoutRef, isHoveringRef }) => {
  const listenersRef = useRef([])

  const handleMarkerLoad = useCallback((marker) => {
    // Clean up any existing listeners
    if (listenersRef.current.length > 0 && window.google?.maps) {
      listenersRef.current.forEach(listener => {
        window.google.maps.event.removeListener(listener)
      })
      listenersRef.current = []
    }
    
    // Add mouse event listeners to the marker using Google Maps event system
    if (marker && window.google?.maps) {
      const mouseoverListener = window.google.maps.event.addListener(marker, 'mouseover', () => {
        isHoveringRef.current = true
        
        // Clear any pending hover out immediately
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current)
          hoverTimeoutRef.current = null
        }
        
        // Show hover immediately - no delay
        // Only show hover if not already selected
        if (!selectedParish || selectedParish.id !== parish.id) {
          onHover(parish)
        }
      })
      
      const mouseoutListener = window.google.maps.event.addListener(marker, 'mouseout', () => {
        // Set flag to false, but delay the actual hide
        // InfoWindow's onMouseEnter will set it back to true if cursor moves there
        isHoveringRef.current = false
        
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current)
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
          // Only hide if flag is still false (cursor didn't move to InfoWindow)
          if (!isHoveringRef.current && (!selectedParish || selectedParish.id !== parish.id)) {
            onHoverOut()
          }
        }, 600)
      })
      
      listenersRef.current = [mouseoverListener, mouseoutListener]
    }
  }, [parish, onHover, onHoverOut, selectedParish, hoverTimeoutRef, isHoveringRef])

  useEffect(() => {
    return () => {
      // Cleanup listeners on unmount
      if (listenersRef.current.length > 0 && window.google?.maps) {
        listenersRef.current.forEach(listener => {
          window.google.maps.event.removeListener(listener)
        })
        listenersRef.current = []
      }
    }
  }, [])

  const handleClick = useCallback(() => {
    // Clear any pending hover actions
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    onHoverOut() // Clear hover when clicking
    onClick()
  }, [onClick, onHoverOut, hoverTimeoutRef])

  return (
    <Marker
      position={{ lat: parish.latitude, lng: parish.longitude }}
      icon={icon}
      title={`${parish.name} - Click for details`}
      onClick={handleClick}
      onLoad={handleMarkerLoad}
      cursor="pointer"
      animation={window.google?.maps?.Animation?.DROP}
      zIndex={1000}
      optimized={false}
    />
  )
}

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '100%',
}

// Default map options - keep default styles, hide points of interest
const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.business',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.attraction',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.place_of_worship',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.school',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.sports_complex',
      elementType: 'all',
      stylers: [{ visibility: 'off' }],
    },
  ],
}

/**
 * ParishMap Component
 * 
 * Displays a Google Maps map showing user location and nearest parishes
 */
export default function ParishMap({ onParishClick }) {
  const navigate = useNavigate()
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  
  // Use useLoadScript hook to handle script loading
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'google-map-script',
  })

  const [userLocation, setUserLocation] = useState(null)
  const [nearestParishes, setNearestParishes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }) // Default to NYC
  const [mapZoom, setMapZoom] = useState(10)
  const [selectedParish, setSelectedParish] = useState(null)
  const [hoveredParish, setHoveredParish] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [currentViewCenter, setCurrentViewCenter] = useState(null)
  const [searchRadius, setSearchRadius] = useState(25) // Default 25 miles
  const [searching, setSearching] = useState(false)
  const hoverTimeoutRef = useRef(null)
  const isHoveringRef = useRef(false) // Track if mouse is over marker or InfoWindow

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const searchParishesAtLocation = useCallback(async (latitude, longitude, radius = searchRadius) => {
    try {
      setSearching(true)
      setError(null)

      const parishes = await api.findNearestParishes({
        latitude,
        longitude,
        limit: 20,
        maxDistance: radius,
      })

      setNearestParishes(parishes || [])
      return parishes || []
    } catch (err) {
      console.error('Failed to fetch nearest parishes:', err)
      setError('Failed to load nearby parishes')
      return []
    } finally {
      setSearching(false)
    }
  }, [searchRadius])

  const getLocationAndParishes = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Get user's current location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords
            const location = { lat: latitude, lng: longitude }
            setUserLocation(location)
            setMapCenter(location)
            setCurrentViewCenter(location)
            setMapZoom(12)

            // Fetch nearest parishes
            await searchParishesAtLocation(latitude, longitude, searchRadius)
            setLoading(false)
          },
          (err) => {
            console.error('Geolocation error:', err)
            setError('Unable to get your location. Please enable location permissions.')
            setLoading(false)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        )
      } else {
        setError('Geolocation is not supported by your browser.')
        setLoading(false)
      }
    } catch (err) {
      console.error('Error getting location:', err)
      setError('Failed to get location')
      setLoading(false)
    }
  }, [searchParishesAtLocation, searchRadius])

  useEffect(() => {
    if (isLoaded) {
      getLocationAndParishes()
    }
  }, [isLoaded, getLocationAndParishes])

  const handleRedoSearch = async () => {
    if (!currentViewCenter) return
    
    await searchParishesAtLocation(
      currentViewCenter.lat,
      currentViewCenter.lng,
      searchRadius
    )
  }

  const mapRef = useRef(null)

  const onLoad = useCallback((map) => {
    mapRef.current = map
    setMapLoaded(true)
    // Get initial center
    const center = map.getCenter()
    if (center) {
      setCurrentViewCenter({ lat: center.lat(), lng: center.lng() })
    }
  }, [])

  const onIdle = useCallback(() => {
    // Called when map finishes moving/zooming
    if (mapRef.current) {
      const center = mapRef.current.getCenter()
      if (center) {
        setCurrentViewCenter({ lat: center.lat(), lng: center.lng() })
      }
    }
  }, [])

  const onUnmount = useCallback(() => {
    setMapLoaded(false)
  }, [])

  // Create custom icons when Google Maps is loaded
  const getUserLocationIcon = () => {
    if (!window.google?.maps) return null
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: '#C5A059',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
    }
  }

  const getParishIcon = () => {
    if (!window.google?.maps) return null
    return {
      url: 'data:image/svg+xml;base64,' + btoa(`
        <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 2C9.37 2 4 7.37 4 14c0 7 12 20 12 20s12-13 12-20c0-6.63-5.37-12-12-12z" 
                fill="#C5A059" 
                stroke="#FFFFFF" 
                stroke-width="2"/>
          <circle cx="16" cy="14" r="4" fill="#FFFFFF"/>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(32, 40),
      anchor: new window.google.maps.Point(16, 40),
    }
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="w-full h-[500px] bg-white border border-surface-border rounded flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-charcoal-subtle mx-auto mb-4" />
          <p className="text-charcoal-muted mb-2">
            Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
          </p>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="w-full h-[500px] bg-white border border-surface-border rounded flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-charcoal-subtle mx-auto mb-4" />
          <p className="text-charcoal-muted mb-2">Error loading Google Maps. Please refresh the page.</p>
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[500px] bg-white border border-surface-border rounded flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-2" />
          <p className="text-charcoal-muted text-sm">Loading map...</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="w-full h-[500px] bg-white border border-surface-border rounded flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-2" />
          <p className="text-charcoal-muted text-sm">Getting your location...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-[500px] bg-white border border-surface-border rounded flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-charcoal-subtle mx-auto mb-4" />
          <p className="text-charcoal-muted mb-2">{error}</p>
          <button
            onClick={getLocationAndParishes}
            className="text-gold hover:text-gold/80 underline text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-[500px] bg-white border border-surface-border rounded overflow-hidden relative">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={mapOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onIdle={onIdle}
      >
          {/* User Location Marker */}
          {userLocation && mapLoaded && (
            <Marker
              position={userLocation}
              icon={getUserLocationIcon()}
              title="Your Location"
            />
          )}

          {/* Parish Markers */}
          {mapLoaded && nearestParishes.map((parish) => {
            if (!parish.latitude || !parish.longitude) return null

            return (
              <HoverableMarker
                key={parish.id}
                parish={parish}
                icon={getParishIcon()}
                selectedParish={selectedParish}
                hoverTimeoutRef={hoverTimeoutRef}
                isHoveringRef={isHoveringRef}
                onHover={(p) => setHoveredParish(p)}
                onHoverOut={() => setHoveredParish(null)}
                onClick={() => {
                  // Navigate directly to parish detail page
                  if (onParishClick) {
                    onParishClick(parish)
                  } else {
                    navigate(`/app/donor/parish/${parish.id}`)
                  }
                }}
              />
            )
          })}

          {/* Info Window for Hovered Parish */}
          {hoveredParish && hoveredParish.latitude && hoveredParish.longitude && !selectedParish && (
            <InfoWindow
              position={{ lat: hoveredParish.latitude, lng: hoveredParish.longitude }}
              onCloseClick={() => setHoveredParish(null)}
            >
              <div 
                className="text-sm p-1"
                onMouseEnter={(e) => {
                  e.stopPropagation()
                  // Set flag to true - we're hovering over InfoWindow
                  isHoveringRef.current = true
                  
                  // Cancel any pending hide timeout when mouse enters InfoWindow
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current)
                    hoverTimeoutRef.current = null
                  }
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation()
                  // Set flag to false
                  isHoveringRef.current = false
                  
                  // Only hide when mouse leaves InfoWindow area
                  // Use a delay to prevent flickering
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current)
                  }
                  hoverTimeoutRef.current = setTimeout(() => {
                    // Only hide if flag is still false
                    if (!isHoveringRef.current && !selectedParish) {
                      setHoveredParish(null)
                    }
                  }, 400)
                }}
              >
                <strong className="text-gray-900 font-semibold">{hoveredParish.name}</strong>
                {hoveredParish.address && (
                  <p className="text-gray-600 mt-1 text-xs">{hoveredParish.address}</p>
                )}
                {hoveredParish.city && hoveredParish.state && (
                  <p className="text-gray-600 text-xs">{hoveredParish.city}, {hoveredParish.state}</p>
                )}
                {hoveredParish.distance !== undefined && (
                  <p className="text-charcoal-muted mt-1 font-semibold text-xs">
                    {hoveredParish.distance.toFixed(1)} miles away
                  </p>
                )}
                <button
                  onClick={() => {
                    if (onParishClick) {
                      onParishClick(hoveredParish)
                    } else {
                      navigate(`/app/donor/parish/${hoveredParish.id}`)
                    }
                    setHoveredParish(null)
                  }}
                  className="mt-2 text-charcoal-muted hover:underline text-xs font-semibold cursor-pointer"
                >
                  View Details →
                </button>
              </div>
            </InfoWindow>
          )}

          {/* Info Window for Selected Parish (on click) */}
          {selectedParish && selectedParish.latitude && selectedParish.longitude && (
            <InfoWindow
              position={{ lat: selectedParish.latitude, lng: selectedParish.longitude }}
              onCloseClick={() => setSelectedParish(null)}
            >
              <div className="text-sm p-1">
                <strong className="text-gray-900 font-semibold">{selectedParish.name}</strong>
                {selectedParish.address && (
                  <p className="text-gray-600 mt-1 text-xs">{selectedParish.address}</p>
                )}
                {selectedParish.city && selectedParish.state && (
                  <p className="text-gray-600 text-xs">{selectedParish.city}, {selectedParish.state}</p>
                )}
                {selectedParish.distance !== undefined && (
                  <p className="text-charcoal-muted mt-1 font-semibold text-xs">
                    {selectedParish.distance.toFixed(1)} miles away
                  </p>
                )}
                <button
                  onClick={() => {
                    if (onParishClick) {
                      onParishClick(selectedParish)
                    } else {
                      navigate(`/app/donor/parish/${selectedParish.id}`)
                    }
                    setSelectedParish(null)
                  }}
                  className="mt-2 text-charcoal-muted hover:underline text-xs font-semibold cursor-pointer"
                >
                  View Details →
                </button>
              </div>
            </InfoWindow>
          )}
      </GoogleMap>

      {/* Map Info Overlay */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm border border-surface-border rounded p-3 z-[1000]">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 rounded-full bg-gold"></div>
          <span className="text-xs text-charcoal-muted">Your Location</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-charcoal-muted" />
          <span className="text-xs text-charcoal-muted">
            {nearestParishes.length} {nearestParishes.length === 1 ? 'Parish' : 'Parishes'} Nearby
          </span>
        </div>
        
        {/* Search Controls */}
        <div className="border-t border-surface-border pt-3 mt-3">
          <label className="block text-xs text-charcoal-muted mb-2">
            Search Radius (miles)
          </label>
          <select
            value={searchRadius}
            onChange={(e) => setSearchRadius(Number(e.target.value))}
            className="w-full bg-white border border-surface-border rounded px-2 py-1 text-xs text-charcoal mb-2 focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value={10}>10 miles</option>
            <option value={25}>25 miles</option>
            <option value={50}>50 miles</option>
            <option value={100}>100 miles</option>
          </select>
          <button
            onClick={handleRedoSearch}
            disabled={searching || !currentViewCenter}
            className="w-full bg-gold text-black text-xs font-semibold px-3 py-2 rounded hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {searching ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <MapPin className="w-3 h-3" />
                Search This Area
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
